import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// Hover any text that is cut off with "…" (a `.truncate` element) to see it in
// full — column names, board / folder / workspace names in the sidebar, group
// titles, the board title. One listener for the whole app, shown only when the
// text really is cut off (Monday-style). Table cells keep their own tooltip
// (marked data-own-tip), and anything with a native title is left alone.
const OverflowTooltip = () => {
  const [tip, setTip] = useState(null); // { text, left, top }

  useEffect(() => {
    let current = null;
    const show = (el) => {
      if (!el || el.closest('[data-own-tip]') || el.closest('[title]')) return null;
      // inline elements can't clip (clientWidth 0) — only measure real boxes
      if (!el.clientWidth || el.scrollWidth <= el.clientWidth + 1) return null;
      // textContent, not innerText: the name as written, not as styled
      // (column headers are shown in CAPITALS).
      const text = (el.textContent || '').trim();
      if (!text) return null;
      const r = el.getBoundingClientRect();
      const below = r.bottom + 6;
      return {
        text,
        left: Math.max(8, Math.min(r.left, window.innerWidth - 340)),
        top: below + 36 > window.innerHeight ? r.top - 36 : below,
      };
    };
    const onOver = (e) => {
      const el = e.target?.closest ? e.target.closest('.truncate') : null;
      if (el === current) return;
      current = el;
      setTip(show(el));
    };
    const hide = () => { current = null; setTip(null); };
    document.addEventListener('mouseover', onOver, true);
    document.addEventListener('mousedown', hide, true);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      document.removeEventListener('mouseover', onOver, true);
      document.removeEventListener('mousedown', hide, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  if (!tip) return null;
  return createPortal(
    <div
      role="tooltip"
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-800 pointer-events-none normal-case tracking-normal font-normal"
      style={{ left: tip.left, top: tip.top, maxWidth: 320 }}
      data-testid="overflow-tooltip"
    >
      {tip.text}
    </div>,
    document.body
  );
};

export default OverflowTooltip;
